import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface PendingContact {
  id: string
  fromUserId: string
  fromUsername: string
  fromRole: string
  toVendorId: string
  requestedAt: number
  message: string
  status: 'pending' | 'approved' | 'denied'
  vendorNotes?: string
}

interface VendorApprovalSystemProps {
  isVendor?: boolean
  userId?: string
}

export default function VendorApprovalSystem({ isVendor = false, userId }: VendorApprovalSystemProps) {
  const { user } = useAuth()
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isVendor && user?.role === 'verified_vendor') {
      loadPendingContacts()
    }
  }, [isVendor, user])

  const loadPendingContacts = () => {
    // Load pending contacts from localStorage (in real app, this would be an API call)
    const stored = localStorage.getItem('pending_vendor_contacts')
    if (stored) {
      const contacts = JSON.parse(stored)
      const userContacts = contacts.filter((c: PendingContact) => 
        c.toVendorId === user?.id && c.status === 'pending'
      )
      setPendingContacts(userContacts)
    }
  }

  const handleApproval = async (contactId: string, approved: boolean, notes?: string) => {
    setLoading(true)
    try {
      // Update contact status
      const stored = localStorage.getItem('pending_vendor_contacts') || '[]'
      const contacts = JSON.parse(stored)
      const updatedContacts = contacts.map((c: PendingContact) => 
        c.id === contactId 
          ? { ...c, status: approved ? 'approved' : 'denied', vendorNotes: notes }
          : c
      )
      
      localStorage.setItem('pending_vendor_contacts', JSON.stringify(updatedContacts))
      
      // Remove from pending list
      setPendingContacts(prev => prev.filter(c => c.id !== contactId))
      
      // If approved, create a conversation
      if (approved) {
        // This would typically create a conversation entry in the messaging system
        console.log(`Approved contact request ${contactId}`)
      }
      
    } catch (error) {
      console.error('Failed to process approval:', error)
    } finally {
      setLoading(false)
    }
  }

  // Function to create a contact request (called when user tries to message vendor)
  const createContactRequest = async (fromUserId: string, toVendorId: string, message: string): Promise<boolean> => {
    try {
      const users = JSON.parse(localStorage.getItem('users') || '[]')
      const fromUser = users.find((u: any) => u.id === fromUserId)
      
      if (!fromUser) return false

      const newContact: PendingContact = {
        id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        fromUserId,
        fromUsername: fromUser.username,
        fromRole: fromUser.role,
        toVendorId,
        requestedAt: Date.now(),
        message: message.slice(0, 200), // Limit message preview
        status: 'pending'
      }

      const stored = localStorage.getItem('pending_vendor_contacts') || '[]'
      const contacts = JSON.parse(stored)
      contacts.push(newContact)
      localStorage.setItem('pending_vendor_contacts', JSON.stringify(contacts))

      return true
    } catch (error) {
      console.error('Failed to create contact request:', error)
      return false
    }
  }

  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#e53e3e'
      case 'moderator': return '#9f7aea'
      case 'verified_vendor': return '#00d166'
      case 'trusted': return '#4299e1'
      default: return '#a0aec0'
    }
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': return '👑'
      case 'moderator': return '🛡️'
      case 'verified_vendor': return '✅'
      case 'trusted': return '⭐'
      default: return ''
    }
  }

  if (!isVendor || user?.role !== 'verified_vendor') {
    return null
  }

  return (
    <div className="vendor-approval-system">
      <div className="approval-header">
        <h3>🛡️ Contact Requests</h3>
        {pendingContacts.length > 0 && (
          <span className="pending-count">{pendingContacts.length} pending</span>
        )}
      </div>

      {pendingContacts.length === 0 ? (
        <div className="no-pending">
          <div className="no-pending-icon">📭</div>
          <h4>No pending contact requests</h4>
          <p>When users want to message you, their requests will appear here for approval.</p>
        </div>
      ) : (
        <div className="pending-contacts">
          {pendingContacts.map(contact => (
            <div key={contact.id} className="contact-request">
              <div className="request-header">
                <div className="user-info">
                  <div className="user-avatar">
                    {contact.fromUsername[0]?.toUpperCase()}
                  </div>
                  <div className="user-details">
                    <div className="username" style={{ color: getRoleColor(contact.fromRole) }}>
                      {getRoleBadge(contact.fromRole)} {contact.fromUsername}
                    </div>
                    <div className="user-role">{contact.fromRole}</div>
                  </div>
                </div>
                <div className="request-time">
                  {formatTimeAgo(contact.requestedAt)}
                </div>
              </div>

              <div className="request-message">
                <h4>Message Preview:</h4>
                <p>"{contact.message}"</p>
              </div>

              <div className="request-actions">
                <button
                  onClick={() => handleApproval(contact.id, true)}
                  disabled={loading}
                  className="approve-btn"
                >
                  ✅ Approve & Start Conversation
                </button>
                <button
                  onClick={() => handleApproval(contact.id, false, 'Not interested')}
                  disabled={loading}
                  className="deny-btn"
                >
                  ❌ Deny Request
                </button>
              </div>

              <div className="protection-info">
                <small>
                  💡 Approving will allow this user to message you directly. 
                  You can always block them later if needed.
                </small>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="approval-settings">
        <h4>⚙️ Contact Settings</h4>
        <div className="settings-options">
          <label className="setting-option">
            <input type="checkbox" defaultChecked />
            <span>Require approval for new user contacts</span>
          </label>
          <label className="setting-option">
            <input type="checkbox" defaultChecked />
            <span>Auto-approve trusted members</span>
          </label>
          <label className="setting-option">
            <input type="checkbox" />
            <span>Allow direct messaging without approval</span>
          </label>
        </div>
      </div>
    </div>
  )
}

// Function to be used by messaging system to check if contact needs approval
export async function requiresVendorApproval(fromUserId: string, toUserId: string): Promise<boolean> {
  // Check if recipient is a vendor
  const users = JSON.parse(localStorage.getItem('users') || '[]')
  const toUser = users.find((u: any) => u.id === toUserId)
  const fromUser = users.find((u: any) => u.id === fromUserId)
  
  if (toUser?.role !== 'verified_vendor') return false
  
  // Check if sender is new user (account age < 7 days)
  if (fromUser?.role === 'member') {
    const accountAge = fromUser.dateCreated ? 
      Math.floor((Date.now() - new Date(fromUser.dateCreated).getTime()) / (24 * 60 * 60 * 1000)) : 0
    
    if (accountAge < 7) return true
  }
  
  // Trusted members and above don't need approval
  if (['trusted', 'verified_vendor', 'moderator', 'admin'].includes(fromUser?.role)) {
    return false
  }
  
  return true
}

// Function to create contact request
export async function createVendorContactRequest(fromUserId: string, toVendorId: string, message: string): Promise<boolean> {
  try {
    const users = JSON.parse(localStorage.getItem('users') || '[]')
    const fromUser = users.find((u: any) => u.id === fromUserId)
    
    if (!fromUser) return false

    const newContact: PendingContact = {
      id: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      fromUserId,
      fromUsername: fromUser.username,
      fromRole: fromUser.role,
      toVendorId,
      requestedAt: Date.now(),
      message: message.slice(0, 200),
      status: 'pending'
    }

    const stored = localStorage.getItem('pending_vendor_contacts') || '[]'
    const contacts = JSON.parse(stored)
    contacts.push(newContact)
    localStorage.setItem('pending_vendor_contacts', JSON.stringify(contacts))

    return true
  } catch (error) {
    console.error('Failed to create contact request:', error)
    return false
  }
}